import {DOCUMENT} from '@angular/common';
import {Component, Inject, Input, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {getLockIconConfig} from '@application/helper/get-lock-icon-config';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {RemoveConfirmationService} from '@application/helper/remove-confirmation/remove-confirmation.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {NavigationBreadcrumbsService} from '@application/services/navigation-breadcrumbs/navigation-breadcrumbs.service';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {Shift} from '@domain/calendar/shift/shift';
import {HttpShiftsService} from '@infrastructure/http/calendar/shift/http-shifts.service';
import {TextileService} from '@presentation/pages/textile-data/textile-data-overview/textile.service';
import {TextileDataType} from '@presentation/pages/textile-data/textile-data-type.enum';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  CanLeavePage,
  ColorConstants,
  ColorPickerMode,
  DialogBuilderFactoryService,
  DialogType,
  EnumUtils,
  FormValidationHelper,
  LockIconConfig,
  NavigationHelperPrototypeService,
  noSpaceValidator,
  PresetColors,
  SaveType,
  TabsMenuItem,
  TranslateService
} from '@vdw/angular-component-library';
import {finalize, Observable, takeUntil} from 'rxjs';

@Component({
  templateUrl: './add-shift-page.component.html'
})
export class AddShiftPageComponent extends BaseComponent implements OnInit, CanLeavePage {
  @Input() public pageType: PageTypeEnum;
  @Input() public itemId: number;
  @Input() public canShowHeader: boolean = false;

  public lockIconConfig: LockIconConfig;
  public selectedMenuItem: TabsMenuItem = {value: 0, translationKey: 'GENERAL.GENERAL'};
  public addShiftForm: FormGroup<{name: FormControl<string>; color: FormControl<string>}>;
  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_SHIFT;
  public readonly SAVE_TYPE = SaveType;
  public readonly SHIFT_TRANSLATION_KEY = 'CALENDAR.SHIFT.SHIFT_CODE';
  public readonly LIMITED = ColorPickerMode.LIMITED;

  private shiftToSave: Shift;

  public constructor(
    @Inject(DOCUMENT) private document: Document,
    public readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly formBuilder: FormBuilder,
    private readonly navigationBreadcrumbsService: NavigationBreadcrumbsService,
    private readonly removeConfirmation: RemoveConfirmationService,
    private readonly router: Router,
    private readonly shifts: HttpShiftsService,
    private readonly textileService: TextileService,
    private readonly translate: TranslateService
  ) {
    super();
  }

  public get createdObject(): string {
    return !AssertionUtils.isNullOrUndefined(this.addShiftForm.controls.name.value) ? this.addShiftForm.controls.name.value : '';
  }

  public ngOnInit(): void {
    this.setFormFields();

    const id = this.activatedRoute.snapshot?.params?.id ?? this.itemId;
    if (!AssertionUtils.isNullOrUndefined(id)) {
      this.shifts
        .getById(parseInt(id))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((shift: Shift) => {
          this.itemId = shift.id;
          this.shiftToSave = shift;
          this.setFormValues();
          this.setLockIconConfig();
        });
    }
  }

  public canLeavePage(): boolean {
    return !this.addShiftForm.dirty || this.saving;
  }

  public saveShift(saveType: SaveType): void {
    const isValid = new FormValidationHelper().checkForms([this.addShiftForm], this.document);
    if (isValid) {
      const shiftToSave = this.getCurrentShift();
      this.saving = true;

      const request: Observable<void | number> = this.isEditingShift() ? this.shifts.update(shiftToSave) : this.shifts.save(shiftToSave);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.isEditingShift() ? this.finalizeSaving() : null)).subscribe({
        next: (id: number) => {
          if (this.navigationHelper.contentSwitcherDialogActive()) {
            if (saveType === SaveType.SAVE_AND_CLOSE) {
              shiftToSave.id = id;
              this.navigationHelper.navigateBack(this.isEditingShift() ? [] : shiftToSave);
            } else if (saveType === SaveType.SAVE_AND_CREATE_NEW) {
              this.pageType = PageTypeEnum.ADD;
              this.setFormFields();
              this.saving = false;
            }
            return;
          }
          this.textileService.navigateAndShowToast(saveType, TextileDataType.SHIFT, this.SHIFT_TRANSLATION_KEY, this.isEditingShift(), shiftToSave.name, id);
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.isEditingShift() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  public duplicateShift(): void {
    if (this.navigationHelper.contentSwitcherDialogActive()) {
      this.navigationHelper.navigateWithContentSwitcher({
        componentInfo: {
          component: AddShiftPageComponent,
          componentInputs: {
            itemId: this.itemId,
            pageType: PageTypeEnum.DUPLICATE,
            canShowHeader: true
          }
        },
        forgetCurrentEntry: true
      });
    } else {
      this.navigationHelper.navigateWithRoute({
        route: RouteUtils.paths.calendar.shift.duplicateShift.absolutePath.replace(':id', this.shiftToSave.id.toString()),
        forgetCurrentEntry: true
      });
    }
  }

  public deleteShift(): void {
    const data = this.getCurrentShift();
    this.removeConfirmation.open(data, this.shifts.delete(data.id), this.SHIFT_TRANSLATION_KEY, null, null, true);
  }

  public navigateBack(): void {
    if (this.navigationHelper.contentSwitcherDialogActive()) {
      this.navigationHelper.navigateBack();
    } else {
      this.router.navigateByUrl(RouteUtils.paths.calendar.shift.absolutePath);
    }
  }

  public getLoadLink(): string {
    return ErrorHandlers.getLoadLink(RouteUtils.paths.calendar.shift.editShift.absolutePath, this.addShiftForm.controls.name);
  }

  public isEditingShift(): boolean {
    if (!AssertionUtils.isNullOrUndefined(this.pageType)) {
      return this.pageType === PageTypeEnum.EDIT;
    }
    return this.activatedRoute.snapshot.routeConfig.path === RouteUtils.paths.calendar.shift.editShift.path;
  }

  public isLocked(): boolean {
    return this.isEditingShift() && this.shiftToSave?.used;
  }

  private setFormFields(): void {
    this.addShiftForm = this.formBuilder.group({
      name: this.formBuilder.control(null, [Validators.required, noSpaceValidator]),
      color: this.formBuilder.control(ColorConstants[`${PresetColors.VIOLET}_500`])
    });

    this.setNameAsyncValidator();

    this.selectedMenuItem.form = this.addShiftForm;
  }

  private setFormValues(): void {
    const name = this.isEditingShift() ? this.shiftToSave.name : null;

    this.addShiftForm.reset({
      name: name,
      color: ColorConstants[`${this.shiftToSave.color}_500`]
    });

    this.setNameAsyncValidator(name);

    if (this.isLocked()) {
      this.addShiftForm.disable();
    }
  }

  private setNameAsyncValidator(name: string = null): void {
    this.addShiftForm.controls.name.setAsyncValidators([AsyncUniqueValidator.createValidator(this.shifts, name)]);
  }

  private getCurrentShift(): Shift {
    return new Shift(
      this.isEditingShift() ? this.shiftToSave.id : null,
      this.addShiftForm.value.name,
      EnumUtils.getKeyFromValue(ColorConstants, this.addShiftForm.controls.color.value).slice(0, -4) as PresetColors
    );
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {
        object: this.translate.instant(this.SHIFT_TRANSLATION_KEY, {count: 1})
      }),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }

  private setLockIconConfig(): void {
    if (this.isLocked()) {
      this.lockIconConfig = getLockIconConfig(this.shiftToSave.name, this.shifts.getConflicts(this.shiftToSave.id));
      this.navigationBreadcrumbsService.onLocked(this.lockIconConfig);
    }
  }
}
